// src/oura/components/newscan/HealthLine.tsx
//
// One line under the Review head that says the 50 are built right: the
// shape ("26 location (14/12) · 14 specialty · 10 intent"), that every outlet
// is asked (Online: every location question names the market), and how many
// questions use the profession word against the recipe's cap. A check that
// fails shows an amber dot and the fact, never a green tick. One fact per
// line (Jack, 20 Oct 2026). On a First AI check whose branches are still
// open, the shape and "every outlet" wait on them: a dashed circle and
// "waiting on the branches", turning to a tick once they are set.
//
// Search Console: connected, how many questions match real searches. That
// count is a filter chip (ink-filled when on): pressed, the 50 below show only
// the questions with a search, most searched first. Not
// connected (new scan and edit only): one blue hint to connect it, marked
// Optional. Nothing here is ever blocked by it. Not on a First AI check: its
// access request comes after the check (JOURNEY-SPEC §2.5).
//
// Brand mode (v4.5, SPEC-v45 §7.1): the shape in plain words ("28 recommend
// you · 14 cite your site · 8 business buyers"), the rows per market, how many
// come from a Google search, no product word in a problem question, no brand
// names or claims. Search Console as above.

import { CheckIcon, CircleDashedIcon, SearchIcon } from "lucide-react";
import type { ReactNode } from "react";
import { isFirstCheckDraft, type ScanDraft } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { Dot, FOCUS_RING, PillButton, Tag, TYPE } from "@oura/ui";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { plural } from "@oura/newscan/copy";
import { shapeFromDraft } from "@oura/newscan/recipeShape";
import { useConnectedFocus } from "@oura/newscan/useConnectedFocus";
import { brandShape, brandShapeOk, homeHoldsFewest, isBrandSet, marketLine, openSlots, traceCounts } from "./brandReview";
import { containsPhrase } from "@oura/newscan/containsPhrase";

const COPY = {
    everyOutlet: "Every outlet asked",
    everyArea: "Every area asked",
    outletsAsked: (k: number, n: number) => `${k} of ${n} outlets asked`,
    everyMarket: "Every question names the market",
    marketMissing: (k: number) => `${plural(k, "location question")} without the market`,
    profession: (used: number) => `Profession word in ${used} ${used === 1 ? "question" : "questions"}`,
    professionMax: (max: number) => `${max} at most`,
    matched: (n: number) => `${n} match real searches`,
    pulling: "Pulling a year of searches",
    hint: "See how often each question is actually searched, and get suggestions from real searches.",
    connect: "Connect Search Console",
    optional: "Optional",
    allTraced: "Every question comes from a Google search",
    traced: (k: number, n: number) => `${k} of ${n} come from a Google search`,
    noCategory: "No problem question names the product",
    category: (n: number) => `${plural(n, "problem question")} ${n === 1 ? "names" : "name"} the product`,
    noClaims: "No brand names or claims in the questions",
    claims: (n: number) => `${plural(n, "question")} ${n === 1 ? "uses" : "use"} your own words`,
    places: (n: number) => `${plural(n, "question")} ${n === 1 ? "names" : "name"} a city`,
    waiting: "waiting on the branches",
} as const;

/** The brand facts, each one line (SPEC-v45 §7.1). */
function BrandFacts({ draft }: { draft: ScanDraft }) {
    const trace = traceCounts(draft);
    const category = openSlots(draft, "category_in_problem_row").length;
    const claims = openSlots(draft, "claim_in_question").length;
    const places = openSlots(draft, "place_in_brand_row").length;
    return (
        <>
            <Fact ok={brandShapeOk(draft)}>{brandShape(draft)}</Fact>
            <Fact ok={homeHoldsFewest(draft)}>
                <span className="tabular-nums">{marketLine(draft)}</span>
            </Fact>
            {trace && (
                <Fact ok={trace.traced === trace.total}>
                    {trace.traced === trace.total ? COPY.allTraced : COPY.traced(trace.traced, trace.total)}
                </Fact>
            )}
            <Fact ok={category === 0}>{category === 0 ? COPY.noCategory : COPY.category(category)}</Fact>
            <Fact ok={claims === 0}>{claims === 0 ? COPY.noClaims : COPY.claims(claims)}</Fact>
            {places > 0 && <Fact ok={false}>{COPY.places(places)}</Fact>}
        </>
    );
}

function Fact({ ok, waiting = false, children }: { ok: boolean; waiting?: boolean; children: ReactNode }) {
    return (
        // Top-aligned: a wrapped line keeps its mark beside its first line.
        <li className="flex min-w-0 items-start gap-1.5">
            {waiting ? (
                <CircleDashedIcon aria-hidden size={14} strokeWidth={2.25} className="mt-[2px] shrink-0 text-gray-400" />
            ) : ok ? (
                <CheckIcon aria-hidden size={14} strokeWidth={2.5} className="mt-[2px] shrink-0 text-emerald-600" />
            ) : (
                <span aria-hidden className="mt-[2px] grid h-3.5 w-3.5 shrink-0 place-items-center">
                    <Dot tone="amber" />
                </span>
            )}
            <span className="min-w-0">
                {children}
                {waiting && <span className="text-gray-400">, {COPY.waiting}</span>}
            </span>
        </li>
    );
}

export interface HealthLineProps {
    draft: ScanDraft;
    /** The 50 show only the questions with a search. */
    searchOnly?: boolean;
    /** Turns the search filter on or off. Without it the count is plain text. */
    onSearchOnly?: (on: boolean) => void;
    /** First AI check: the branches are not set yet, so the outlet facts wait on them. */
    branchesOpen?: boolean;
}

export function HealthLine({ draft, searchOnly = false, onSearchOnly, branchesOpen = false }: HealthLineProps) {
    const { kind, gsc, connectGsc, ctl } = useScanFlow();
    const search = draft.search;
    const focus = useConnectedFocus<HTMLLIElement>(search.connected);
    const b = draft.business;
    const location = draft.questions.filter((q) => q.check === "location");
    const brand = isBrandSet(draft);

    let places: ReactNode;
    if (brand) {
        places = null;
    } else if (b.buy === "online") {
        const market = b.market?.trim() ?? "";
        const missing = market ? location.filter((q) => !containsPhrase(q.text, market)).length : 0;
        places = <Fact ok={missing === 0}>{missing === 0 ? COPY.everyMarket : COPY.marketMissing(missing)}</Fact>;
    } else {
        const outlets = b.outlets.filter((o) => o.name.trim() && (o.source === "site" || o.confirmed === true));
        const asked = new Set(location.map((q) => q.fill.toLowerCase()));
        const k = outlets.filter((o) => asked.has(o.name.trim().toLowerCase())).length;
        const all = k === outlets.length;
        places = outlets.length > 0 && (
            <Fact ok={all} waiting={branchesOpen}>
                {all ? (b.buy === "we_go_to_them" ? COPY.everyArea : COPY.everyOutlet) : COPY.outletsAsked(k, outlets.length)}
            </Fact>
        );
    }

    // Brand mode has no profession cap: a problem question never names the product at all.
    // No category set and no profession word in any question (a showroom set): there is nothing to count, so no tick.
    const capWord = brand ? null : (draft.words.find((w) => w.capWarn) ?? null);
    const capFound = capWord?.capWarn ?? null;
    const noCategory = !b.category.trim() || !capWord?.value.trim();
    const cap = capFound && capFound.used === 0 && noCategory ? null : capFound;
    const connecting = gsc.status === "connecting" || ctl.busy === "Checking searches";

    return (
        <div className="space-y-3">
            <ul role="list" className={cn(TYPE.tc, "list-plain m-0 flex flex-col gap-y-1.5 p-0 text-gray-600")}>
                {brand ? <BrandFacts draft={draft} /> : <Fact ok waiting={branchesOpen}>{shapeFromDraft(draft)}</Fact>}
                {places}
                {cap && (
                    <Fact ok={cap.used <= cap.warnAbove}>
                        <span className="tabular-nums">{COPY.profession(cap.used)}</span>
                        <span className="text-gray-400">, {COPY.professionMax(cap.warnAbove)}</span>
                    </Fact>
                )}
                {search.connected && (
                    <li ref={focus.targetRef} tabIndex={-1} className="flex items-center gap-1.5 text-blue-700 outline-none">
                        {!search.pulling && search.matched > 0 && onSearchOnly ? (
                            <button
                                type="button"
                                aria-pressed={searchOnly}
                                onClick={() => onSearchOnly(!searchOnly)}
                                className={cn(
                                    TYPE.tc,
                                    "relative inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full px-3 font-medium transition-colors",
                                    "after:absolute after:-inset-y-2 after:inset-x-0 after:content-['']",
                                    searchOnly
                                        ? "bg-gray-900 text-white"
                                        : "bg-white text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50",
                                    FOCUS_RING,
                                )}
                            >
                                <SearchIcon aria-hidden size={14} className="shrink-0" />
                                <span className="tabular-nums">{COPY.matched(search.matched)}</span>
                            </button>
                        ) : (
                            <>
                                <SearchIcon aria-hidden size={14} className="shrink-0" />
                                {search.pulling ? COPY.pulling : <span className="tabular-nums">{COPY.matched(search.matched)}</span>}
                            </>
                        )}
                    </li>
                )}
            </ul>
            {!search.connected && kind !== "recipe_change" && !isFirstCheckDraft(draft) && (
                <div
                    className={cn(
                        TYPE.tc,
                        "flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-xl bg-blue-50/60 px-3 py-2.5 ring-1 ring-inset ring-blue-100",
                    )}
                >
                    <SearchIcon aria-hidden size={14} className="shrink-0 text-blue-600" />
                    <span className="min-w-0 flex-1 basis-60 text-gray-700">{COPY.hint}</span>
                    <span className="inline-flex items-center gap-2">
                        <PillButton
                            variant="text"
                            busy={connecting}
                            onClick={() => {
                                focus.asked();
                                void connectGsc();
                            }}
                        >
                            {COPY.connect}
                        </PillButton>
                        <Tag tone="gray">{COPY.optional}</Tag>
                    </span>
                    {gsc.error && (
                        <p role="alert" className="m-0 basis-full font-medium text-rose-600">
                            {gsc.error}
                        </p>
                    )}
                </div>
            )}
            <span role="status" className="sr-only">
                {focus.said}
            </span>
        </div>
    );
}
